import React, { useEffect, useRef, useState } from 'react';
import TextArea from './TextArea';
import { CodeOutlined, Save, DriveFileRenameOutlineOutlined, AddOutlined, AutoAwesomeOutlined, LockOpenOutlined, LockOutlined, WarningAmberOutlined, UnfoldMoreOutlined, UnfoldLessOutlined } from '@mui/icons-material';
import ExecutionPlanTab from './ExecutionPlanTab';
import ResultTab from './ResultTab';
import { CircularProgress, IconButton, Tooltip, Button, Autocomplete, TextField, Box, DialogContentText, Typography } from '@mui/material';
import ExecutionSummary from './ExecutionSummary';
import { fetchTestCaseScenario, updateExecutionDetails, updateExecutionPlan, updateTestCaseScenario, uploadScreenshot } from '../../../utils/api';
import { getVariableListData } from '../../../utils/api/Variable_apis';
import { fetchEnvironmentList } from '../../../utils/api/environment_apis';
import { formatDuration } from '../../../utils/commonfunctions';
import { SparklesIcon, CheckIcon, PlayIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { useSnackbar } from '../../../utils/useSnackbar';
import SmartTextArea from './SmartTextArea';
import InformationPopup from './InformationPopup';
import TestStepForm from './TestStepForm';
import { STATUS } from '../../../utils/api/sharedfunction';

const AnimatedStep = ({ children, delay }) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);
  return (
    <div
      style={{
        opacity: show ? 1 : 0,
        marginTop: show ? '0px' : '20px',
        transition: 'opacity 0.5s ease, margin-top 0.5s ease',
      }}    
    >
      {children}
    </div>
  );
};

// Reusable primary action button for all main actions
const PrimaryActionButton = ({
  onClick,
  disabled,
  loading,
  icon,
  label,
  className = '',
  style = {},
  ...rest
}) => (
  <button
    onClick={disabled || loading ? undefined : onClick}
    disabled={disabled || loading}
    className={`flex items-center px-4 py-2 rounded-md border font-medium transition-all duration-200 ${className} ${disabled || loading ? 'opacity-50 cursor-not-allowed' : ''}`}
    style={style}
    {...rest}
  >
    {loading ? (
      <CircularProgress size={18} sx={{ color: "#ffffff!important", marginRight: '10px' }} />
    ) : (
      icon
    )}
    {label}
  </button>
);

const TestcaseAutomationTab = ({testCaseId}) => {
  const socketRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const [testSteps, setTestSteps] = useState('');
  const [isEditing, setIsEditing] = useState(true); // Start unlocked by default
  const [hasChanges, setHasChanges] = useState(false); // Track if user has made changes
  const [generatePlanLoading, setGeneratePlanLoading] = useState(false);
  const [automationRunning, setAutomationRunning] = useState(false);
  const [executionPlan, setExecutionPlan] = useState([]);
  const [stepResults, setStepResults] = useState([]);
  const [environmentId, setEnvironmentId] = useState(null);
  const [environmentList, setEnvironmentList] = useState([]);
  const [playwrightScript, setPlaywrightScript] = useState('');
  const [executionSummary, setExecutionSummary] = useState(null);
  const [executionPlanId, setExecutionPlanId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [executionDeatilSaveLoading, setExecutionDeatilSaveLoading] = useState(false);
  const [isSavedScenario, setIsSavedScenario] = useState(false);
  const [isSavedExecutionPlan, setIsSavedExecutionPlan] = useState(false);
  const [toolsData, setToolsData] = useState([]);
  const [variablesList, setVariablesList] = useState([]);
  const executionPlanTabRef = useRef(null);
  const { showSnackbar } = useSnackbar();
  const [isOpen, setIsOpen] = useState(false);
  const [informationPopupData, setInformationPopupData] = useState({
    title: 'Information Popup',
    content: '',
  });
  const toggleInformationPopup = () => {
    setIsOpen(prev => !prev);
  };
  const stepsRef = useRef(testSteps);


  const handleInformationPopupClick = (data) => {
    toggleInformationPopup();
    let title = '';
    let fields = [];
    switch(data.type){
      case 'INSUFFICIENT_INFO': 
        title='Insufficient Information';
        fields=[
          {
            Component: Typography,
            name: 'subheading',
            label: "Subheading",
            children: 'Reason:',
            sx: {
              fontWeight: 500,
              marginBottom: '4px',
            },
          },
          {
            Component: DialogContentText,
            name: 'info',
            label: "Info",
            children: data?.executionInfo.reason || '',
          },
          {
            Component: Typography,
            name: 'subheading',
            label: "Subheading",
            children: 'Suggestions:',
            sx: {
              fontWeight: 500,
              marginTop: '10px',
              marginBottom: '4px',
            },
          },
          {
            Component: DialogContentText,
            name: 'info',
            label: "Info",
            children: data?.executionInfo.suggestions || '',
          },
        ];
        break;
      case 'BUSINESS_LOGIC_ERROR':
        title='Business Logic Error';
        fields=[
          {
            Component: DialogContentText,
            name: 'info',
            label: "Info",
            children: data?.executionInfo.businessLogicFailure || '',
          },
        ];
        break;
      default:
        title='Information Popup';  
        break;
    }
    setInformationPopupData({
      title: title,
      fields: fields,
    });
  }


  const grayBorderStyle = {
    border: '1px solid rgb(222, 226, 233)',
    borderRadius: '8px',
    padding: '1.5rem',
  };

  useEffect(() => {
    (async ()=>{
      const response = await getVariableListData();
      const { data = [], status, message, error } = await fetchEnvironmentList();
      if(status === STATUS.SUCCESS){
        setEnvironmentList([{id: null, name: 'None'}, ...data] || []);
      } else{
        setEnvironmentList([{id: null, name: 'None'}])
      }
      if(response.status === STATUS.SUCCESS){
        setVariablesList(response.data);
      }
    })()
  }, []);

  useEffect(() => {
    if(!socketRef.current){
      connectToSocket();
    }
    return () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      if (socketRef.current) {
        try { socketRef.current.close(); } catch (_) {}
        socketRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    (async ()=>{
      const response = await fetchTestCaseScenario({testCaseId});
      if(response?.status){
        const {testScenario='', excutionSteps=[], planId=null, executionResult=null} = response?.data || {};
        if(testScenario && testScenario.length > 0){
          setTestSteps(testScenario);
          stepsRef.current = testScenario
          setIsSavedScenario(true);
          setIsEditing(false); // Lock if data is loaded from API
          setHasChanges(false); // No changes initially
        } else {
          setIsEditing(true); // Unlock if no data, allow editing
          setHasChanges(false);
        }
        if(excutionSteps?.length > 0){
          const tExcutionSteps = excutionSteps.map((step, index) => {
            return {
              ...step,
              id: step?.id || Date.now() + Math.random(), // Ensure unique ID
              description: step?.description || '',
              shouldAdapt: step?.shouldAdapt ?? true,
              custom_step: step?.custom_step ?? false,
              expanded: false, // Default to collapsed
              editMode: false, // Default to not editing,
            }
          });
          setExecutionPlan(tExcutionSteps); 
          setIsSavedExecutionPlan(true);
        }
        if(planId){
          setExecutionPlanId(planId);
        }
        if(executionResult){
          // Process saved execution steps from database
          if(executionResult.ExecutionSteps && executionResult.ExecutionSteps.length > 0) {
            const savedStepResults = executionResult.ExecutionSteps.map((step, index) => ({
              stepNumber: step.sort_order,
              description: step.description,
              status: step.status,
              duration: step.duration,
              tool: step.tool,
              arguments: step.step_arguments,
              screenshot: step.screenshot_link, // This is now the S3 file key
              logs: step.logs || [], // Load saved logs from database
              isSavedResult: true // Flag to identify saved results
            }));
            setStepResults(savedStepResults);
          } else {
            // Fallback to parsing result field if no execution steps
            (()=>{
              try {
                const tData = JSON.parse(executionResult.result); 
                setStepResults(tData);
              } catch (error) {
                console.error('Error parsing execution result:', error);
              }
            })();
          }
          
          (()=>{
            try {
              // execution_summary is already parsed as JSONB from database
              const tData = typeof executionResult.execution_summary === 'string' 
                ? JSON.parse(executionResult.execution_summary) 
                : executionResult.execution_summary;
              setExecutionSummary(tData);
            } catch (error) {
              console.error('Error parsing execution summary:', error);
            }
          })();
        }
      }
    })()
  }, [testCaseId]);

  // Debounced change detection for better UX
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isEditing && testSteps.trim().length > 0) {
        setHasChanges(true);
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [testSteps, isEditing]);

  function startPing() {
    if (pingIntervalRef.current) return;
    pingIntervalRef.current = setInterval(() => {
      const ws = socketRef.current;
      if (ws && ws.readyState === 1) {
        try { ws.send(JSON.stringify({ type: 'ping' })); } catch (_) {}
      }
    }, 250000);
  }

  function stopPing() {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  }

  function connectToSocket() {
    socketRef.current = new WebSocket("ws://localhost:8080/ap/ws");
    // socketRef.current = new WebSocket("wss://ap-agent.qa-path.com/ap/ws");

    if(socketRef.current){
      socketRef.current.onopen = () => {
        startPing();
        socketRef.current.send(JSON.stringify({ type: "fetch_tools" }));
        socketRef.current.onmessage = ((event)=>{
          const msg = (() => {
            try {
              return JSON.parse(event.data);
            } catch (err) {
              return null;
            }
          })();
          if(msg?.type === "tools"){
            let tTools = (()  =>{
              try {
                setToolsData(eval(msg?.data));
              } catch (err) {
                return null;
              }
            })();
          }
          })
        };
    }
  }

  async function handleSaveAutomationPlan() {
    setIsSaving(true);
    // Filter out steps that are in edit mode
    const stepsToSave = executionPlan.filter(step => !step.editMode);
    try {
      const response = await updateExecutionPlan({testCaseId: testCaseId, executionSteps: stepsToSave});
      if(response?.status){
        setExecutionPlanId(response?.planId);
        setIsSavedExecutionPlan(true);
        showSnackbar('Execution Plan saved successfully', 'success');
      }
    } catch (error) {
      showSnackbar(error?.response?.data?.message || 'Failed to save execution plan', 'error');
    } finally {
      setIsSaving(false);
    }
  }


  function socketOnOpen() {
    startPing();
    socketRef.current.send(JSON.stringify({ type: "generate_plan", testSteps: stepsRef.current }));
  }
  function socketOnError() {
    // setPlanError("WebSocket error while generating plan.");
    setGeneratePlanLoading(false);
  }

  function socketOnClose(event) {
    stopPing();
    if (event.code !== 1000) { // 1000 is normal closure
      const reason = (()=>{
        try {
          return JSON.parse(event.reason);
        } catch (err) {
          return {};
        }
      })();
      socketRef.current = null;
      setGeneratePlanLoading(false);
      setAutomationRunning(false);
      setIsSaving(false);
      setExecutionDeatilSaveLoading(false);
      if(reason?.errorType){
        showSnackbar(reason?.msg || 'Connection lost. Please try again.', 'error');
      } else {
        console.log('Connection lost. Please try again');
      }
      if(reason?.errorType === 'Unauthorized_Error'){
        window.location.href = '/logout';
      }
    }
  }

  function socketOnMessage(event) {
    const msg = (() => {
      try {
        return JSON.parse(event.data);
      } catch (err) {
        return null;
      }
    })();
    if(event.type === "connection_status"){
      setToolsData(event.data);
    }
    if (!msg) return;
    if(msg.type === "plan_generated"){
        if(msg.data.status === "error"){
          setGeneratePlanLoading(false);
          setIsSaving(false);
          showSnackbar(msg.data.message || 'Failed to generate plan', 'error');
        } else if (msg.data.status === "success"){
          const tExecutionPlan = (msg?.data?.plan?.steps || []).map((step, index) => {
            return {
              ...step,
              shouldAdapt: true,
              custom_step:false,
            }
          });
          setExecutionPlan(tExecutionPlan || []);
          setGeneratePlanLoading(false);
          setIsSaving(false);
          setHasChanges(false); // Reset changes after successful plan generation
        }
    }
  }

  function socketOnAutomationMessage(event) {
    const msg = (() => {
      try {
        return JSON.parse(event.data);
      } catch (err) {
        return null;
      }
    })();
    if(msg.type === "execution_status"){
      if(msg.data.status === "running"){
        showSnackbar(msg.data.message, 'info');
      } else if (msg.data.status === "completed"){
        setAutomationRunning(false);
        showSnackbar(msg.data.message, 'success');
        // setStepResults(msg.data.steps);
      } else if (msg.data.status === "error"){
        setAutomationRunning(false);
      } else if(msg.data.status == 'missing_variables'){
        setAutomationRunning(false);
        showSnackbar(msg.data.message, 'error');
      }
    }
    if(msg.type === "execution_log"){
      setStepResults(prev => {
        const tempObj = msg.data.step;
        let updated = [...prev];
        const lastStep = updated?.length > 0 ? updated[updated.length - 1] : null;
        if(lastStep?.actualStepNumber === tempObj?.actualStepNumber && (tempObj?.retryAttempt ? tempObj?.retryAttempt == lastStep?.retryAttempt : true)){
          updated[updated.length - 1] = {
            ...lastStep,
            ...tempObj
          }
          return [...updated];
        } else {
          return [...updated, tempObj];
        }
      })
    }
    if(msg.type === "execution_summary"){
      if(msg.data.status === "success"){
        setExecutionSummary(msg.data?.summary || { });
      }
      setAutomationRunning(false);
    }
    if (msg.type === "script") {
      setPlaywrightScript(msg.data);
    }
  }

  function addSocketEvent(type) {
    switch(type) {
      case 'generatePlan':
        socketRef.current.onopen = socketOnOpen;
        socketRef.current.onmessage = socketOnMessage;
        socketRef.current.onerror = socketOnError;
        socketRef.current.onclose = socketOnClose;
        break;
      case 'startAutomation':
        socketRef.current.onmessage = socketOnAutomationMessage;
        socketRef.current.onerror = socketOnError;
        socketRef.current.onclose = socketOnClose;
        break;
    }
  }

  function handleStartAutomation() {
    setStepResults([]);
    setExecutionSummary(null);
    setAutomationRunning(true);
    if (!socketRef.current || socketRef.current.readyState !== 1) {
      connectToSocket();
      socketRef.current.onopen = () => {
        startPing();
        socketRef.current.send(JSON.stringify({ type: "execute_plan", plan: {steps: executionPlan}, environmentId: environmentId }));
      };
    } else {
      socketRef.current.send(JSON.stringify({ type: "execute_plan", plan: {steps: executionPlan}, environmentId: environmentId }));

    }
    addSocketEvent('startAutomation');
  }

  function handleStopPlanGeneration() {
    // if (socketRef.current) {
    //   socketRef.current.send(JSON.stringify({ type: "stopPlanGeneration"}));
    //   socketRef.current = null; 
    // }
  }

  function handleStopAutomation() {
    if (socketRef.current) {
      socketRef.current.send(JSON.stringify({ type: "stopPlanGeneration"}));
      stopPing();
      socketRef.current = null; 
    }
  }

  async function handleGenerateAutomationPlan(event, save=false) {
    setIsSaving(true);
    setIsSavedScenario(true);
    setExecutionPlan([]);
    setStepResults([]);
    setExecutionSummary(null);
    setGeneratePlanLoading(true);
    connectToSocket();
    addSocketEvent('generatePlan');
  }

  function handleTestStepInputChange(value) {
    setTestSteps(value);
  }

  async function handleSaveTestSteps() {
    await updateTestCaseScenario({
      testCaseId,
      data: stepsRef.current
    })
  }

  function handleSaveEditToggle() {
    if (hasChanges) {
      // If there are changes, save first
      handleSaveAndLock();
    } else {
      // If no changes, just toggle lock/unlock
      setIsEditing(prev => !prev);
    }
  }

  async function handleSaveAndLock() {
    setIsSaving(true);
    try {
      await handleSaveTestSteps(); // Only save steps, do not generate plan
      setHasChanges(false);
      setIsEditing(false);
      showSnackbar('Execution steps saved successfully', 'success');
    } catch (error) {
      console.error('Error saving:', error);
      showSnackbar(error?.response?.data?.message || 'Failed to save execution steps', 'error');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSaveExecutionDetails() {
    setExecutionDeatilSaveLoading(true);
    const tScreenshot = stepResults.map((step, index) => {
      return {
        screenshot: step.screenshot,
        stepIndex: step.actualStepNumber
      }
    })
    const screenshotData = {};
    const chunkSize = 20;
    for (let i = 0; i < tScreenshot.length; i += chunkSize) {
      const chunk = tScreenshot.slice(i, i + chunkSize);
      await Promise.all(chunk.map(async (screenshot) => {
        if(screenshot.screenshot && screenshot.stepIndex) {
          const response = await uploadScreenshot({
            base64Screenshot: screenshot.screenshot,
            executionResultId: stepResults[0].id,
            stepIndex: screenshot.stepIndex,
          })
          if(response.status == 1){
            const {fileKey, stepIndex} = response.data;
            screenshotData[stepIndex] = fileKey;
          }
        }
      }));
    }
    const tExecutionResult = stepResults.map((step, index) => {
      return {
        ...step,
        description: step.description,
        status: step.status,
        duration: step.duration,
        logs: step.logs || [], // Ensure logs are included when saving
        tool: step.tool,
        arguments: step.arguments,
        screenshot: screenshotData[step.actualStepNumber] || null,
        screenshotLink: screenshotData[step.actualStepNumber] || null, // Include screenshot data for S3 upload
      }
    })
    let response = null;
    try {
      response = await updateExecutionDetails({
        testcaseId: testCaseId,
        executionResult: tExecutionResult,
        executionSummary: executionSummary,
      });

      if(response.status == 1){
        showSnackbar('Execution Result saved successfully', 'success');
        return
      } 
      if(!response.error){  
        showSnackbar('Execution Result saved successfully', 'success')
      }
      // showSnackbar(response?.error || 'Failed to update execution details', 'error')
    } catch (error) {
      showSnackbar(error?.response?.data?.message || 'Failed to update execution details', 'error');
    } finally {
      setExecutionDeatilSaveLoading(false);
    }
  }

  function handleAddStep(type = 'add', afterIndex = null) {
    
    const newStep = {
      id: Date.now() + Math.random(), // Unique ID for each step
      description: "",
      tool: "",
      arguments: {},
      tenchnicalImplementation: "",
      expanded: true,
      editMode: true // New steps should be in edit mode
    };


    setExecutionPlan(prev => {
      const newPlan = [...prev];
      
      switch(type) {
        case 'start':
          newPlan.unshift(newStep);
          break;
        case 'add':
          if (afterIndex !== null && afterIndex >= 0 && afterIndex < newPlan.length) {
            // Insert after the specified index
            newPlan.splice(afterIndex + 1, 0, newStep);
          } else {
            // Add to the end
            newPlan.push(newStep);
          }
          break;
        case 'edit':
          // This case is handled separately for editing existing steps
          break;
        default:
          newPlan.push(newStep);
      }
      
      return newPlan;
    });

    // Reset saved state when plan is modified
    setIsSavedExecutionPlan(false);
  }

  function handleDeleteStep(stepIndex) {
      setExecutionPlan(prev => {
        const newPlan = prev.filter((_, index) => index !== stepIndex);
        setIsSavedExecutionPlan(false);
        return newPlan;
      });
  }

  function handleUpdateStep(stepIndex, updatedStep) {
    setExecutionPlan(prev => {
      const newPlan = [...prev];
      newPlan[stepIndex] = {
        ...newPlan[stepIndex],
        ...updatedStep,
        editMode: false // Exit edit mode after update
      };
      setIsSavedExecutionPlan(false);
      return newPlan;
    });
  }

  function handleUpdateStepAiAdapt(stepIndex, isAiAdapt) {
    setExecutionPlan(prev => {
      const newPlan = [...prev];
      newPlan[stepIndex] = {
        ...newPlan[stepIndex],
        shouldAdapt: isAiAdapt
      };
      return newPlan;
    });
  }

  function handleUpdateStepCustomStep(stepIndex, isCustomStep) {
    setExecutionPlan(prev => {
      const newPlan = [...prev];
      newPlan[stepIndex] = {
        ...newPlan[stepIndex],
        custom_step: isCustomStep,
        shouldAdapt: !isCustomStep ? true : newPlan[stepIndex].shouldAdapt
      };
      return newPlan;
    });
  }

  function handleToggleStepEditMode(stepIndex, editMode) {
    setExecutionPlan(prev => {
      const newPlan = [...prev];
      newPlan[stepIndex] = {
        ...newPlan[stepIndex],
        editMode
      };
      return newPlan;
    });
  }

  function handleToggleStepExpanded(stepIndex, expanded) {
    setExecutionPlan(prev => {
      const newPlan = [...prev];
      newPlan[stepIndex] = {
        ...newPlan[stepIndex],
        expanded
      };
      return newPlan;
    });
  }

  function handleExpandAll() {
    setExecutionPlan(prev => 
      prev.map(step => ({
        ...step,
        expanded: true
      }))
    );
  }

  function handleCollapseAll() {
    setExecutionPlan(prev => 
      prev.map(step => ({
        ...step,
        expanded: false
      }))
    );
  }

  // Check if all steps are expanded or collapsed for button states
  const allExpanded = executionPlan.length > 0 && executionPlan.every(step => step.expanded);
  const allCollapsed = executionPlan.length > 0 && executionPlan.every(step => !step.expanded);
  
  // Intelligent toggle function that expands all if any are collapsed, otherwise collapses all
  function handleToggleExpandCollapseAll() {
    if (allExpanded) {
      handleCollapseAll();
    } else {
      handleExpandAll();
    }
  }

  // Download execution plan as JSON
  const [isDownloading, setIsDownloading] = useState(false);
  
  async function handleDownloadExecutionPlan() {
    setIsDownloading(true);
    
    try {
      // Clean the execution plan data - remove UI-specific properties
      const cleanedPlan = executionPlan.map((step, index) => ({
        step: index + 1,
        description: step.description || '',
        tool: step.tool || '',
        arguments: step.arguments || {},
        technicalImplementation: step.tenchnicalImplementation || ''
      }));

      // Create the JSON structure with metadata
      const exportData = {
        metadata: {
          testCaseId: testCaseId,
          exportedAt: new Date().toISOString(),
          totalSteps: executionPlan.length
        },
        automationPlan: cleanedPlan
      };

      // Create and download the file
      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `automation-plan-${testCaseId}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      // Small delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 800));
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <>
    <div style={{ padding: '20px' }}>


      {/* Editable Test Steps TextArea */}
      <div className="relative" style={{ marginBottom: 10 }}>
        <TestStepForm
          suggestions={variablesList?.data || []}
          descriptions={variablesList?.description || {}}
          testCaseId={testCaseId}
          data={testSteps}
          stepsRef={stepsRef}
        />

        {/* <SmartTextArea 
          suggestions={variablesList?.data || []}
          descriptions={variablesList?.description || {}}
          isEditable={isEditing}
          displayValue={testSteps}
          onChange={handleTestStepInputChange}
        /> */}
        {/* <span
          className="absolute right-3 top-3 text-gray-400 pointer-events-none flex items-center justify-center"
          style={{
            width: '2rem',
            height: '2rem',
            pointerEvents: 'none',
            background: 'transparent',
          }}
        >
          <Tooltip title={
            isSaving ? "Saving..." : 
            hasChanges ? "Save changes" : 
            isEditing ? "Lock editing" : "Unlock editing"
          }>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'auto',
              }}
              onClick={isSaving ? undefined : handleSaveEditToggle}
              tabIndex={0}
              role="button"
              aria-label={
                isSaving ? "Saving..." : 
                hasChanges ? "Save changes" : 
                isEditing ? "Lock editing" : "Unlock editing"
              }
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  if (!isSaving) {
                    handleSaveEditToggle();
                  }
                }
              }}
            >
              {isSaving ? (
                <CircularProgress size={20} sx={{ color: "#727f8f" }} />
              ) : hasChanges ? (
                <CheckIcon style={{ width: 20, height: 20, color: "#4CAF50" }} />
              ) : isEditing ? (
                <LockOpenOutlined sx={{ color: "#727f8f" }} />
              ) : (
                <LockOutlined sx={{ color: "#727f8f" }} />
              )}
            </span>
          </Tooltip>
        </span> */}
      </div>


      {/* Action Row: Save first for better result + buttons */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
        <span style={{ color: '#64748b', fontSize: 14, fontWeight: 400 }}>
          Describe your test steps in plain English. Save them first, then generate the automation plan.
        </span>
        <div className="flex gap-2">
        {/* <button 
          className={`flex items-center justify-center bg-green-600 text-white px-4 py-2 rounded-lg border transition-all duration-200 ${!generatePlanLoading ? '' : 'opacity-50 cursor-not-allowed'}`} 
          onClick={(e)=>handleGenerateAutomationPlan(e, true)}
          disabled={generatePlanLoading}
          style={{ minWidth: '140px' }} // Optional: ensures consistent button size
        >
          {isSaving && generatePlanLoading ? (
            <CircularProgress size={22} sx={{ color: "#ffffff!important" }} />
          ) : (
            <>
              <CheckIcon width={18} height={18} className="mr-2" />
              Save Steps
            </>
          )}
        </button> */}

        <PrimaryActionButton
          onClick={(e)=>handleGenerateAutomationPlan(e, true)}
          disabled={generatePlanLoading || automationRunning || isSaving}
          loading={generatePlanLoading}
          icon={<AutoAwesomeOutlined className="mr-2" />}
          label={executionPlan && executionPlan.length > 0 ? 'Re-Generate' : 'Generate'}
          className="bg-[#2664eb] text-white border-[#2664eb]"
          style={{ minWidth: '140px' }}
        />

        </div>
      </div>

      {/* <button className='bg-[#eb5a0c] text-white px-4 py-2 rounded-md border border-[#eb5a0c]' onClick={handleStopPlanGeneration}>Stop Plan Generation</button> */}
      <div className='mb-6'>
        {executionPlan.length > 0 && <div style={grayBorderStyle}>
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
              <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <span style={{ fontWeight: 500, fontSize: 20, color: '#0f172a', marginBottom: 6, paddingRight: 20 }}>
                  Automation Plan
                </span>
 
              </span>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Tooltip title="Download Automation Plan as JSON">
                <Button
                  onClick={executionPlan.length === 0 || isDownloading ? undefined : handleDownloadExecutionPlan}
                  disabled={executionPlan.length === 0 || isDownloading}
                  startIcon={isDownloading ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <ArrowDownTrayIcon style={{ width: 16, height: 16 }} />}
                  variant="sectionButton"
                  sx={{
                    opacity: executionPlan.length === 0 ? 0.4 : 1,
                    cursor: executionPlan.length === 0 || isDownloading ? 'not-allowed' : 'pointer'
                  }}
                >
                  <span>JSON</span>
                </Button>            
              </Tooltip>
              
              <Button
                onClick={() => handleAddStep('start')}
                startIcon={<AddOutlined sx={{ width: 16, height: 16 }} />}
                variant="sectionButton"
              >
                <span>Add Step</span>
              </Button>
              </div>
            </div>
            
            {/* Expand/Collapse All Steps - positioned before steps rendering */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
              <span
                onClick={executionPlan.length === 0 ? undefined : handleToggleExpandCollapseAll}
                className={`flex items-center gap-1 text-sm transition-colors ${
                  executionPlan.length === 0 
                    ? 'opacity-30 cursor-default text-gray-400' 
                    : 'text-green-600 hover:text-green-700 cursor-pointer'
                }`}
                style={{ fontSize: '12px' }}
              >
                <span style={{ fontSize: '12px' }}>
                  {allExpanded ? '▼' : '▶'}
                </span>
                {allExpanded ? 'Collapse all' : 'Expand all'}
              </span>
            </div>
            
            <>
            {
              executionPlan.map((data, index) => (
                <AnimatedStep key={data.id || index} delay={index * 120}>
                  <ExecutionPlanTab
                    variableSuggestions={variablesList?.data || []}
                    variableDescriptions={variablesList?.description || {}}
                    ref={executionPlanTabRef}
                    toolsData={toolsData}
                    step={index+1}
                    description={data.description}
                    tool={data.tool}
                    shouldAdapt={data.shouldAdapt}
                    customStep={data.custom_step}
                    argument={data.arguments}
                    tenchnicalImplementation={data.tenchnicalImplementation}
                    expanded={data.expanded || false}
                    editMode={data.editMode || false}
                    stepIndex={index}
                    onUpdateStep={handleUpdateStep}
                    onDeleteStep={handleDeleteStep}
                    onToggleEditMode={handleToggleStepEditMode}
                    onToggleExpanded={handleToggleStepExpanded}
                    onAddStepAfter={handleAddStep}
                    onUpdateStepAiAdapt={handleUpdateStepAiAdapt}
                    onUpdateStepCustomStep={handleUpdateStepCustomStep}
                  />
                 </AnimatedStep>
              ))
            }
            </>

            <div className="mt-2 flex gap-2 justify-end mt-3 items-center">
              {/* <div style={{ minWidth: 320, maxWidth: 400, width: 400 }}> */}
                <Autocomplete
                  options={environmentList || []}
                  getOptionLabel={(option) => option?.name || ''}
                  value={environmentList?.find(env => env.id === environmentId) || null}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Environment" variant="outlined" size="small" />
                  )}
                  onChange={(event, value) => {
                    setEnvironmentId(value?.id ?? null);
                  }}
                  placeholder="Select Environment"
                  sx={{ width: '190px' }}
                />
              {/* </div> */}
              <PrimaryActionButton
                onClick={handleSaveAutomationPlan}
                disabled={isSaving || !isSavedScenario || executionPlan.some(step => step.editMode) || automationRunning}
                loading={isSaving}
                icon={<CheckIcon style={{width: 18, height: 18, marginRight: 6}}/>}
                label="Save Plan"
                className="bg-green-600 text-white"
                style={{ minWidth: '140px' }}
              />
              <PrimaryActionButton
                onClick={automationRunning ? undefined : handleStartAutomation}
                disabled={automationRunning}
                loading={automationRunning}
                icon={<PlayIcon style={{ width: 18, height: 18, marginRight: 6 }} />}
                label="Execute Plan"
                className="bg-[#2664eb] text-white border-[#2664eb] min-w-[10rem]"
                style={{ minWidth: '160px' }}
              />
            </div>
        </div>}
        
        {executionPlan.length === 0 && (
          <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <AutoAwesomeOutlined sx={{color: "#9ca3af", fontSize: '3rem', marginBottom: '1rem'}}/>
            <p className="text-gray-500 text-lg mb-2">No automation steps yet</p>
            <p className="text-gray-400 text-sm mb-4">Click "Add Step" to create your first automation step</p>
            <div className="flex gap-2 justify-center">
              {/* <button 
                className='flex items-center gap-2 text-[#2d6aed] hover:bg-[#f0f4ff] px-4 py-2 rounded-md transition-colors'
                onClick={() => handleAddStep('start')}
              >
                <AddOutlined sx={{color: "#2d6aed"}}/>
                <span>Add Step at Start</span>
              </button> */}
              <button 
                className='flex items-center gap-2 text-[#2d6aed] hover:bg-[#f0f4ff] px-4 py-2 rounded-md transition-colors'
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleAddStep('start');
                }}
              >
                <AddOutlined sx={{color: "#2d6aed"}}/>
                <span>Add Step</span>
              </button>
            </div>
          </div>
        )}
      </div>
      
      {stepResults.length > 0 && (
        <div style={{ ...grayBorderStyle, padding: '1.5rem', marginBottom: 24 }}>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: 15 }}>
            <span style={{ fontWeight: 500, fontSize: 20, color: '#0f172a', marginBottom: 6, paddingRight: 32 }}>
              Execution Results
            </span>
          </div>
          <ResultTab stepResults={stepResults} isSavedResult={stepResults.some(step => step.isSavedResult)} onInformationPopupClick={handleInformationPopupClick} />
        </div>
      )}
      {executionSummary && Object.keys(executionSummary).length > 0 && (
        <>
          {/* <div className='mb-6 mt-6'>
            <h3 className='text-[#272f3d] font-semibold text-lg'>Execution Summary</h3>
            <span className='text-[#7b8a99] text-sm'>Execution Summary</span>
          </div> */}
          <ExecutionSummary executionSummary={executionSummary} />
        </>
      )}
      {stepResults.length > 0 && !automationRunning && !stepResults.some(step => step.isSavedResult) && 
        <div className="flex gap-2 justify-end mt-6">
          <PrimaryActionButton
            onClick={(!isSavedExecutionPlan || !executionPlanId || executionDeatilSaveLoading || automationRunning) ? undefined : handleSaveExecutionDetails}
            disabled={!isSavedExecutionPlan || !executionPlanId || executionDeatilSaveLoading || automationRunning}
            loading={executionDeatilSaveLoading}
            icon={<CheckIcon style={{width: 18, height: 18, marginRight: 6}}/>}
            label="Save Execution Details"
            className="mt-2 w-[15rem] bg-green-600 text-white"
            style={{ minWidth: '180px' }}
          />
        </div>
      }
    </div>
    <InformationPopup
      isOpen={isOpen}
      onClose={toggleInformationPopup}
      title={informationPopupData?.title || ''}
      fields={informationPopupData?.fields || []}
      content={informationPopupData?.content || ''}
    />
    </>
  );
};

export default TestcaseAutomationTab;
